'use client';

import { CheckCircle, Loader2, AlertCircle, Scissors, Edit3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useMomento } from '@/hooks/useMomento';
import { toast } from 'sonner';
import { useEffect } from 'react';

/**
 * Tool UI for Create Memo tool
 * Displays memo creation status and automatically switches to memo view
 */
export function CreateMemoToolUI({ part, status }: any) {
  const { setActiveMemo } = useMomento();
  const result = part.result;

  // Auto-fetch and set active memo when creation succeeds
  useEffect(() => {
    if (status === 'done' && result?.success && result?.memoId) {
      console.log('[CreateMemoToolUI] Memo created:', result.memoId);

      // Fetch full memo data
      fetch(`/api/memo/${result.memoId}`)
        .then(res => res.json())
        .then(data => {
          if (data.memo) {
            setActiveMemo(data.memo);
            toast.success('✨ Memo created! Opening editor...');
          }
        })
        .catch(err => {
          console.error('[CreateMemoToolUI] Failed to fetch memo:', err);
          toast.error('Failed to load memo editor');
        });
    }
  }, [status, result, setActiveMemo]);

  if (status === 'loading' || !result) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
        <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
        <span className="text-sm text-purple-900 dark:text-purple-100">
          Creating memo...
        </span>
      </div>
    );
  }

  if (!result.success) {
    return (
      <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-red-900 dark:text-red-100">
            Failed to create memo
          </p>
          {result.error && (
            <p className="text-xs text-red-700 dark:text-red-300 mt-1">
              {result.error}
            </p>
          )}
        </div>
      </div>
    );
  }

  const metadata = result.metadata || {};
  const cutCount = metadata.cutCount || 0;
  const captionEditCount = metadata.captionEditCount || 0;
  const effectiveDuration = metadata.effectiveDuration || 0;
  const originalDuration = metadata.originalDuration || 0;
  const timeSaved = originalDuration - effectiveDuration;

  return (
    <div className="rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 overflow-hidden">
      <div className="flex items-center gap-2 p-3">
        <CheckCircle className="w-4 h-4 text-purple-600 flex-shrink-0" />
        <span className="text-sm font-medium text-purple-900 dark:text-purple-100">
          Memo created successfully!
        </span>
      </div>

      {(cutCount > 0 || captionEditCount > 0) && (
        <div className="px-3 pb-3 space-y-2">
          <div className="flex items-center gap-4 text-xs text-purple-700 dark:text-purple-300">
            {cutCount > 0 && (
              <div className="flex items-center gap-1">
                <Scissors className="w-3 h-3" />
                <span>{cutCount} cut{cutCount !== 1 ? 's' : ''}</span>
              </div>
            )}
            {captionEditCount > 0 && (
              <div className="flex items-center gap-1">
                <Edit3 className="w-3 h-3" />
                <span>{captionEditCount} caption edit{captionEditCount !== 1 ? 's' : ''}</span>
              </div>
            )}
            {timeSaved > 0 && (
              <div className="text-xs">
                <span className="font-medium">{timeSaved.toFixed(1)}s</span> saved
              </div>
            )}
          </div>

          {metadata.wordsDeleted && metadata.wordsDeleted.length > 0 && (
            <div className="text-xs text-purple-600 dark:text-purple-400">
              Removed: {metadata.wordsDeleted.join(', ')}
            </div>
          )}

          {metadata.wordsNotFound && metadata.wordsNotFound.length > 0 && (
            <div className="text-xs text-yellow-600 dark:text-yellow-400">
              Not found: {metadata.wordsNotFound.join(', ')}
            </div>
          )}
        </div>
      )}

      <div className="px-3 pb-3">
        <p className="text-xs text-purple-600 dark:text-purple-400">
          Opening memo editor on the right →
        </p>
      </div>
    </div>
  );
}

/**
 * Tool UI for Update Memo tool
 * Displays memo update status
 */
export function UpdateMemoToolUI({ part, status }: any) {
  const { setActiveMemo } = useMomento();
  const result = part.result;

  // Auto-update active memo when update succeeds
  useEffect(() => {
    if (status === 'done' && result?.success && result?.updatedMemo) {
      console.log('[UpdateMemoToolUI] Memo updated:', result.updatedMemo.id);
      setActiveMemo(result.updatedMemo);
      toast.success('✨ Memo updated!');
    }
  }, [status, result, setActiveMemo]);

  if (status === 'loading' || !result) {
    return (
      <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <Loader2 className="w-3 h-3 animate-spin text-blue-600" />
        <span className="text-xs text-blue-900 dark:text-blue-100">
          Updating memo...
        </span>
      </div>
    );
  }

  if (!result.success) {
    return (
      <div className="flex items-start gap-2 p-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
        <AlertCircle className="w-3 h-3 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-xs font-medium text-red-900 dark:text-red-100">
            Failed to update memo
          </p>
          {result.error && (
            <p className="text-xs text-red-700 dark:text-red-300 mt-1">
              {result.error}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
      <CheckCircle className="w-3 h-3 text-green-600" />
      <span className="text-xs text-green-900 dark:text-green-100">
        Memo updated successfully!
      </span>
    </div>
  );
}
